import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Chip,
  Snackbar,
  TextField,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Delete,
  Visibility,
  Download,
  ArrowBack,
  Lock,
  CheckCircle,
} from '@mui/icons-material';
import axios from 'axios';

interface ScoutSummary {
  pending: number;
  read: number;
  accepted: number;
  declined: number;
  blocked: number;
  contact_shared: number;
}

interface ResultSummary {
  id: number;
  name: string;
  age: number;
  gender: string;
  sport: string;
  years_of_experience: number;
  competition_level: string;
  email: string;
  created_at: string;
  scout_summary: ScoutSummary | null;
}

interface ScoutDetail {
  company_name: string;
  status: string;
  contact_shared: boolean;
}

interface ResultDetail {
  id: number;
  name: string;
  age: number;
  gender: string;
  sport: string;
  years_of_experience: number;
  competition_level: string;
  email: string;
  basic_abilities: Record<string, number>;
  riasec_profile: Record<string, number>;
  work_values: Record<string, number>;
  portable_skills: Record<string, number>;
  strengths: string[];
  development_areas: string[];
  athlete_advantages: string[];
  career_matches: Array<{
    career: { name: string };
    matchScore: number;
  }>;
  created_at: string;
  scout_details: ScoutDetail[] | null;
}

interface CompanySummary {
  id: number;
  uuid: string;
  email: string;
  company_name: string;
  contact_person: string;
  phone: string | null;
  website: string | null;
  plan_state: string;
  email_verified_at: string | null;
  created_at: string;
}

interface CompanyDetail extends CompanySummary {
  description: string | null;
  updated_at: string;
  scout_count: number;
  view_count: number;
}

const API_BASE = '/api';

const getAuthHeaders = () => {
  const password = sessionStorage.getItem('adminPassword');
  return password ? { Authorization: `Bearer ${password}` } : {};
};

const planStateLabel: Record<string, { label: string; color: 'default' | 'primary' | 'secondary' | 'warning' | 'error' }> = {
  free_beta: { label: '無料ベータ', color: 'default' },
  basic: { label: 'ベーシック', color: 'primary' },
  premium: { label: 'プレミアム', color: 'secondary' },
  suspended: { label: '停止中', color: 'error' },
};

const AdminPage: React.FC = () => {
  const navigate = useNavigate();
  const [tabIndex, setTabIndex] = useState(0);

  // 診断結果関連
  const [results, setResults] = useState<ResultSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedDetail, setSelectedDetail] = useState<ResultDetail | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ResultSummary | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // 企業情報関連
  const [companies, setCompanies] = useState<CompanySummary[]>([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [companiesError, setCompaniesError] = useState('');
  const [selectedCompany, setSelectedCompany] = useState<CompanyDetail | null>(null);
  const [companyDetailOpen, setCompanyDetailOpen] = useState(false);
  const [companiesFetched, setCompaniesFetched] = useState(false);

  // 認証関連
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [authLoading, setAuthLoading] = useState(true);
  const [authError, setAuthError] = useState('');

  const fetchResults = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE}/results.php`, { headers: getAuthHeaders() });
      setResults(response.data.data.results);
      setError('');
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 401) {
        setAuthenticated(false);
        sessionStorage.removeItem('adminPassword');
        return;
      }
      setError('データの取得に失敗しました。');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCompanies = useCallback(async () => {
    try {
      setCompaniesLoading(true);
      const response = await axios.get(`${API_BASE}/admin-companies.php`, { headers: getAuthHeaders() });
      setCompanies(response.data.data.companies);
      setCompaniesError('');
      setCompaniesFetched(true);
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 401) {
        setAuthenticated(false);
        sessionStorage.removeItem('adminPassword');
        return;
      }
      setCompaniesError('企業データの取得に失敗しました。');
    } finally {
      setCompaniesLoading(false);
    }
  }, []);

  // ページロード時にsessionStorageのパスワードで自動認証試行
  useEffect(() => {
    const savedPassword = sessionStorage.getItem('adminPassword');
    if (savedPassword) {
      axios.get(`${API_BASE}/results.php`, { headers: { Authorization: `Bearer ${savedPassword}` } })
        .then((response) => {
          setAuthenticated(true);
          setResults(response.data.data.results);
        })
        .catch(() => {
          sessionStorage.removeItem('adminPassword');
        })
        .finally(() => {
          setAuthLoading(false);
        });
    } else {
      setAuthLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authenticated) {
      fetchResults();
    }
  }, [authenticated, fetchResults]);

  // 企業タブに切り替わったとき、まだ取得していなければ取得
  useEffect(() => {
    if (authenticated && tabIndex === 1 && !companiesFetched) {
      fetchCompanies();
    }
  }, [authenticated, tabIndex, companiesFetched, fetchCompanies]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError('');
    try {
      await axios.get(`${API_BASE}/results.php`, { headers: { Authorization: `Bearer ${password}` } });
      sessionStorage.setItem('adminPassword', password);
      setAuthenticated(true);
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 401) {
        setAuthError('パスワードが正しくありません');
      } else {
        setAuthError('サーバーへの接続に失敗しました');
      }
    }
  };

  const handleViewDetail = async (id: number) => {
    try {
      const response = await axios.get(`${API_BASE}/result-detail.php?id=${id}`, { headers: getAuthHeaders() });
      setSelectedDetail(response.data.data);
      setDetailOpen(true);
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 401) {
        setAuthenticated(false);
        sessionStorage.removeItem('adminPassword');
        return;
      }
      setSnackbar({ open: true, message: '詳細の取得に失敗しました', severity: 'error' });
    }
  };

  const handleViewCompanyDetail = async (id: number) => {
    try {
      const response = await axios.get(`${API_BASE}/admin-companies.php?id=${id}`, { headers: getAuthHeaders() });
      setSelectedCompany(response.data.data);
      setCompanyDetailOpen(true);
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 401) {
        setAuthenticated(false);
        sessionStorage.removeItem('adminPassword');
        return;
      }
      setSnackbar({ open: true, message: '企業詳細の取得に失敗しました', severity: 'error' });
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.post(`${API_BASE}/delete-result.php`, { id: deleteTarget.id }, { headers: getAuthHeaders() });
      setSnackbar({ open: true, message: `${deleteTarget.name}さんのデータを削除しました`, severity: 'success' });
      setDeleteTarget(null);
      fetchResults();
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 401) {
        setAuthenticated(false);
        sessionStorage.removeItem('adminPassword');
        return;
      }
      setSnackbar({ open: true, message: '削除に失敗しました', severity: 'error' });
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await axios.get(`${API_BASE}/export-csv.php`, {
        headers: getAuthHeaders(),
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'assessment_results.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 401) {
        setAuthenticated(false);
        sessionStorage.removeItem('adminPassword');
        return;
      }
      setSnackbar({ open: true, message: 'CSVエクスポートに失敗しました', severity: 'error' });
    }
  };

  // 初期認証チェック中
  if (authLoading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  // パスワード入力画面
  if (!authenticated) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
        <Box sx={{ bgcolor: '#0066cc', color: 'white', py: 3, boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
          <Container maxWidth="lg">
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, fontSize: { xs: '1.5rem', md: '2rem' } }}>
              データ管理
            </Typography>
          </Container>
        </Box>
        <Container maxWidth="sm" sx={{ py: 8 }}>
          <Paper sx={{ p: 4 }}>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Lock sx={{ fontSize: 48, color: '#0066cc', mb: 1 }} />
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                管理者認証
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                データ管理画面にアクセスするにはパスワードを入力してください
              </Typography>
            </Box>
            {authError && <Alert severity="error" sx={{ mb: 2 }}>{authError}</Alert>}
            <form onSubmit={handleLogin}>
              <TextField
                fullWidth
                type="password"
                label="パスワード"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                sx={{ mb: 2 }}
                autoFocus
              />
              <Button type="submit" variant="contained" fullWidth size="large" disabled={!password}>
                ログイン
              </Button>
            </form>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      {/* ヘッダー */}
      <Box sx={{ bgcolor: '#0066cc', color: 'white', py: 3, boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
        <Container maxWidth="lg">
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600, fontSize: { xs: '1.5rem', md: '2rem' } }}>
            データ管理
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* アクションバー */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Button startIcon={<ArrowBack />} onClick={() => navigate('/')}>
            ホームに戻る
          </Button>
          {tabIndex === 0 && (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button variant="contained" startIcon={<Download />} onClick={handleExportCSV} disabled={results.length === 0}>
                CSVエクスポート
              </Button>
            </Box>
          )}
        </Box>

        {/* タブ切替 */}
        <Paper sx={{ mb: 3 }}>
          <Tabs
            value={tabIndex}
            onChange={(_, newValue) => setTabIndex(newValue)}
            sx={{
              '& .MuiTab-root': { fontWeight: 600, fontSize: '1rem' },
            }}
          >
            <Tab label="診断結果" />
            <Tab label="企業情報" />
          </Tabs>
        </Paper>

        {/* 診断結果タブ */}
        {tabIndex === 0 && (
          <>
            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
              </Box>
            ) : results.length === 0 ? (
              <Paper sx={{ p: 6, textAlign: 'center' }}>
                <Typography variant="h6" color="text.secondary">
                  まだ診断結果がありません
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  診断を実行すると、ここに結果が表示されます
                </Typography>
              </Paper>
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 600 }}>ID</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>名前</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>年齢</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>競技</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>競技レベル</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>診断日時</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>スカウト状況</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="center">操作</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {results.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell>{row.id}</TableCell>
                        <TableCell>{row.name}</TableCell>
                        <TableCell>{row.age}</TableCell>
                        <TableCell>{row.sport}</TableCell>
                        <TableCell>{row.competition_level}</TableCell>
                        <TableCell>{row.created_at}</TableCell>
                        <TableCell>
                          {row.scout_summary === null ? (
                            <Typography variant="body2" color="text.disabled">-</Typography>
                          ) : (() => {
                            const s = row.scout_summary;
                            const total = s.pending + s.read + s.accepted + s.declined + s.blocked;
                            if (total === 0) {
                              return <Typography variant="body2" color="text.secondary">スカウトなし</Typography>;
                            }
                            const chips: Array<{ label: string; color: 'default' | 'primary' | 'success' | 'error' | 'warning' | 'info' }> = [];
                            if (s.accepted > 0) chips.push({ label: `承認 ${s.accepted}`, color: 'success' });
                            if (s.declined > 0) chips.push({ label: `辞退 ${s.declined}`, color: 'error' });
                            if (s.blocked > 0) chips.push({ label: `ブロック ${s.blocked}`, color: 'warning' });
                            if (s.contact_shared > 0) chips.push({ label: `共有済 ${s.contact_shared}`, color: 'info' });
                            if (s.pending > 0) chips.push({ label: `未読 ${s.pending}`, color: 'default' });
                            if (s.read > 0) chips.push({ label: `既読 ${s.read}`, color: 'primary' });
                            return (
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                {chips.map((c, i) => (
                                  <Chip key={i} label={c.label} size="small" color={c.color} variant="outlined" />
                                ))}
                              </Box>
                            );
                          })()}
                        </TableCell>
                        <TableCell align="center">
                          <IconButton color="primary" onClick={() => handleViewDetail(row.id)} title="詳細表示">
                            <Visibility />
                          </IconButton>
                          <IconButton color="error" onClick={() => setDeleteTarget(row)} title="削除">
                            <Delete />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <Box sx={{ mt: 2, textAlign: 'right' }}>
              <Typography variant="body2" color="text.secondary">
                全 {results.length} 件
              </Typography>
            </Box>
          </>
        )}

        {/* 企業情報タブ */}
        {tabIndex === 1 && (
          <>
            {companiesError && <Alert severity="error" sx={{ mb: 3 }}>{companiesError}</Alert>}

            {companiesLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
              </Box>
            ) : companies.length === 0 ? (
              <Paper sx={{ p: 6, textAlign: 'center' }}>
                <Typography variant="h6" color="text.secondary">
                  まだ企業が登録されていません
                </Typography>
              </Paper>
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 600 }}>ID</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>企業名</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>担当者</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>メール</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>電話</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Webサイト</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>プラン</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>登録日</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="center">操作</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {companies.map((row) => {
                      const plan = planStateLabel[row.plan_state] || { label: row.plan_state, color: 'default' as const };
                      return (
                        <TableRow key={row.id} hover>
                          <TableCell>{row.id}</TableCell>
                          <TableCell>{row.company_name}</TableCell>
                          <TableCell>{row.contact_person}</TableCell>
                          <TableCell>{row.email}</TableCell>
                          <TableCell>{row.phone || '-'}</TableCell>
                          <TableCell>
                            {row.website ? (
                              <a href={row.website} target="_blank" rel="noopener noreferrer" style={{ color: '#0066cc' }}>
                                {row.website.replace(/^https?:\/\//, '').slice(0, 30)}
                              </a>
                            ) : '-'}
                          </TableCell>
                          <TableCell>
                            <Chip label={plan.label} size="small" color={plan.color} />
                          </TableCell>
                          <TableCell>{row.created_at}</TableCell>
                          <TableCell align="center">
                            <IconButton color="primary" onClick={() => handleViewCompanyDetail(row.id)} title="詳細表示">
                              <Visibility />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <Box sx={{ mt: 2, textAlign: 'right' }}>
              <Typography variant="body2" color="text.secondary">
                全 {companies.length} 件
              </Typography>
            </Box>
          </>
        )}
      </Container>

      {/* 診断結果 詳細ダイアログ */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        {selectedDetail && (
          <>
            <DialogTitle sx={{ fontWeight: 600 }}>
              {selectedDetail.name}さんの診断結果
            </DialogTitle>
            <DialogContent dividers>
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>基本情報</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip label={`${selectedDetail.age}歳`} size="small" />
                  <Chip label={selectedDetail.gender} size="small" />
                  <Chip label={selectedDetail.sport} size="small" color="primary" />
                  <Chip label={`競技歴 ${selectedDetail.years_of_experience}年`} size="small" />
                  <Chip label={selectedDetail.competition_level} size="small" variant="outlined" />
                </Box>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>RIASECプロファイル</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {Object.entries(selectedDetail.riasec_profile).map(([key, val]) => (
                    <Chip key={key} label={`${key}: ${val}`} size="small" variant="outlined" />
                  ))}
                </Box>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>ポータブルスキル</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {Object.entries(selectedDetail.portable_skills).map(([key, val]) => (
                    <Chip key={key} label={`${key}: ${val}`} size="small" variant="outlined" />
                  ))}
                </Box>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>強み</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {selectedDetail.strengths.map((s, i) => (
                    <Chip key={i} label={s} size="small" color="success" />
                  ))}
                </Box>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>伸ばせる領域</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {selectedDetail.development_areas.map((s, i) => (
                    <Chip key={i} label={s} size="small" color="info" />
                  ))}
                </Box>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>アスリートとしての強み</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {selectedDetail.athlete_advantages.map((s, i) => (
                    <Chip key={i} label={s} size="small" color="warning" />
                  ))}
                </Box>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>マッチング職業（上位）</Typography>
                {selectedDetail.career_matches.map((m, i) => (
                  <Typography key={i} variant="body2" sx={{ mb: 0.5 }}>
                    {i + 1}. {m.career.name}（{m.matchScore}%）
                  </Typography>
                ))}
              </Box>

              {/* スカウト状況セクション */}
              {(() => {
                const resultRow = results.find(r => r.id === selectedDetail.id);
                const ss = resultRow?.scout_summary;
                if (ss === undefined) return null;
                return (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>スカウト状況</Typography>
                    {ss === null ? (
                      <Typography variant="body2" color="text.disabled">アカウント未登録</Typography>
                    ) : (() => {
                      const total = ss.pending + ss.read + ss.accepted + ss.declined + ss.blocked;
                      if (total === 0) {
                        return <Typography variant="body2" color="text.secondary">スカウトなし</Typography>;
                      }
                      return (
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {ss.accepted > 0 && <Chip label={`承認 ${ss.accepted}`} size="small" color="success" />}
                          {ss.declined > 0 && <Chip label={`辞退 ${ss.declined}`} size="small" color="error" />}
                          {ss.blocked > 0 && <Chip label={`ブロック ${ss.blocked}`} size="small" color="warning" />}
                          {ss.contact_shared > 0 && <Chip label={`連絡先共有済 ${ss.contact_shared}`} size="small" color="info" />}
                          {ss.pending > 0 && <Chip label={`未読 ${ss.pending}`} size="small" />}
                          {ss.read > 0 && <Chip label={`既読 ${ss.read}`} size="small" color="primary" />}
                        </Box>
                      );
                    })()}
                  </Box>
                );
              })()}

              {/* スカウト詳細リスト */}
              {selectedDetail.scout_details && selectedDetail.scout_details.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>スカウト詳細</Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#fafafa' }}>
                          <TableCell sx={{ fontWeight: 600 }}>企業名</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>ステータス</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>連絡先共有</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedDetail.scout_details.map((sd, i) => {
                          const statusMap: Record<string, { label: string; color: 'default' | 'primary' | 'success' | 'error' | 'warning' }> = {
                            pending: { label: '未読', color: 'warning' },
                            read: { label: '既読', color: 'primary' },
                            accepted: { label: '承認', color: 'success' },
                            declined: { label: '辞退', color: 'error' },
                            blocked: { label: 'ブロック', color: 'error' },
                          };
                          const st = statusMap[sd.status] || { label: sd.status, color: 'default' as const };
                          return (
                            <TableRow key={i}>
                              <TableCell>{sd.company_name}</TableCell>
                              <TableCell>
                                <Chip label={st.label} size="small" color={st.color} variant="outlined" />
                              </TableCell>
                              <TableCell>
                                {sd.contact_shared ? (
                                  <Chip icon={<CheckCircle />} label="共有済" size="small" color="info" />
                                ) : (
                                  <Typography variant="body2" color="text.disabled">未共有</Typography>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetailOpen(false)}>閉じる</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* 企業詳細ダイアログ */}
      <Dialog open={companyDetailOpen} onClose={() => setCompanyDetailOpen(false)} maxWidth="md" fullWidth>
        {selectedCompany && (
          <>
            <DialogTitle sx={{ fontWeight: 600 }}>
              {selectedCompany.company_name}
            </DialogTitle>
            <DialogContent dividers>
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>基本情報</Typography>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, width: 160, border: 'none', pl: 0 }}>企業名</TableCell>
                      <TableCell sx={{ border: 'none' }}>{selectedCompany.company_name}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, border: 'none', pl: 0 }}>担当者</TableCell>
                      <TableCell sx={{ border: 'none' }}>{selectedCompany.contact_person}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, border: 'none', pl: 0 }}>メール</TableCell>
                      <TableCell sx={{ border: 'none' }}>{selectedCompany.email}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, border: 'none', pl: 0 }}>電話</TableCell>
                      <TableCell sx={{ border: 'none' }}>{selectedCompany.phone || '-'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, border: 'none', pl: 0 }}>Webサイト</TableCell>
                      <TableCell sx={{ border: 'none' }}>
                        {selectedCompany.website ? (
                          <a href={selectedCompany.website} target="_blank" rel="noopener noreferrer" style={{ color: '#0066cc' }}>
                            {selectedCompany.website}
                          </a>
                        ) : '-'}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, border: 'none', pl: 0 }}>プラン</TableCell>
                      <TableCell sx={{ border: 'none' }}>
                        <Chip
                          label={(planStateLabel[selectedCompany.plan_state] || { label: selectedCompany.plan_state }).label}
                          size="small"
                          color={(planStateLabel[selectedCompany.plan_state] || { color: 'default' as const }).color}
                        />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, border: 'none', pl: 0 }}>メール認証</TableCell>
                      <TableCell sx={{ border: 'none' }}>
                        {selectedCompany.email_verified_at ? (
                          <Chip label="認証済み" size="small" color="success" />
                        ) : (
                          <Chip label="未認証" size="small" color="warning" />
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, border: 'none', pl: 0 }}>登録日</TableCell>
                      <TableCell sx={{ border: 'none' }}>{selectedCompany.created_at}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>

              {selectedCompany.description && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>企業説明</Typography>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {selectedCompany.description}
                    </Typography>
                  </Paper>
                </Box>
              )}

              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>利用状況</Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Paper variant="outlined" sx={{ p: 2, flex: 1, textAlign: 'center' }}>
                    <Typography variant="h5" sx={{ fontWeight: 600, color: '#0066cc' }}>
                      {selectedCompany.scout_count}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      スカウト送信数
                    </Typography>
                  </Paper>
                  <Paper variant="outlined" sx={{ p: 2, flex: 1, textAlign: 'center' }}>
                    <Typography variant="h5" sx={{ fontWeight: 600, color: '#0066cc' }}>
                      {selectedCompany.view_count}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      プロフィール閲覧数
                    </Typography>
                  </Paper>
                </Box>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setCompanyDetailOpen(false)}>閉じる</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* 削除確認ダイアログ */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>削除の確認</DialogTitle>
        <DialogContent>
          <Typography>
            {deleteTarget?.name}さんの診断結果を削除しますか？この操作は取り消せません。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>キャンセル</Button>
          <Button onClick={handleDelete} color="error" variant="contained">削除</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminPage;
